import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useEditMode } from '@/contexts/EditModeContext';

const ServiceCard = ({ service, variant = 'default' }) => {
  const { isEditMode, sendEditMessage } = useEditMode();
  
  const IconMap = {
    'users': () => <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
    'user-check': () => <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11l2 2 4-4" /></svg>,
    'package': () => <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>,
    'search': () => <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>,
    'trending-up': () => <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>,
    'share-2': () => <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>,
    'file-text': () => <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
    'target': () => <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
    'mic': () => <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>,
    'briefcase': () => <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
  };

  const Icon = IconMap[service.icon] || IconMap['briefcase'];
  
  // Determine the correct link path based on slug
  let linkPath;
  if (service.slug === 'workshop') {
    linkPath = '/service/workshop';
  } else if (service.slug === 'career-strategy-session' || service.slug === 'career-coaching-packages') {
    linkPath = `/coaching/${service.slug}`;
  } else {
    linkPath = `/service/${service.slug}`;
  }

  const handleEditClick = (e) => {
    if (isEditMode) {
      e.preventDefault();
      e.stopPropagation();
      sendEditMessage('service', service.id, service);
    }
  };

  const editOverlay = isEditMode && (
    <div className="absolute inset-0 border-2 border-dashed border-transparent group-hover:border-blue-500 rounded-lg transition-all duration-200 pointer-events-none z-10">
      <div className="absolute -top-8 left-0 bg-blue-500 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
        Edit: {service.title}
      </div>
    </div>
  );

  if (variant === 'image') {
    return (
      <Link 
        to={isEditMode ? '#' : linkPath} 
        onClick={handleEditClick}
        className="block relative" 
        data-testid={`service-card-${service.slug}`}
      >
        <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer h-full">
          {editOverlay}
          <div className="relative h-48 overflow-hidden">
            <img
              src={service.image_url || 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=640'}
              alt={service.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          </div>
          <CardContent className="p-6">
            <h3 className="text-xl font-heading font-bold mb-2 group-hover:text-primary transition-colors">
              {service.title}
            </h3>
            <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
              {service.short_description}
            </p>
            <span className="inline-flex items-center text-primary font-medium text-sm group-hover:gap-2 transition-all">
              Read More <ArrowRight className="w-4 h-4 ml-1" />
            </span>
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <Link 
      to={isEditMode ? '#' : linkPath} 
      onClick={handleEditClick}
      className="block relative" 
      data-testid={`service-card-${service.slug}`}
    >
      <Card className="group p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer h-full relative">
        {editOverlay}
        <div className="text-primary mb-4">
          <Icon />
        </div>
        <h3 className="text-xl font-heading font-bold mb-2 group-hover:text-primary transition-colors">
          {service.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
          {service.short_description}
        </p>
        <span className="inline-flex items-center text-primary font-medium text-sm group-hover:gap-2 transition-all">
          Read More <ArrowRight className="w-4 h-4 ml-1" />
        </span>
      </Card>
    </Link>
  );
};

export default ServiceCard;
